import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Layout from '../../components/Layout'
import TransactionModal from '../../components/TransactionModal'
import ExposureLimitModal from '../../components/ExposureLimitModal'
import CreditModal from '../../components/CreditModal'
import PasswordModal from '../../components/PasswordModal'
import ChangeStatusModal from '../../components/ChangeStatusModal'
import userService from '../../services/userService'
import walletService from '../../services/walletService'
import Toast from '../../utils/toast'
import { isUserRole } from '../../utils/roles'
import './style.css'

/**
 * Users Component
 * 
 * Displays list of clients/users (Account List).
 */
function Users() {
  const { user } = useSelector((state) => state.auth)
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [searchValue, setSearchValue] = useState('')
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [showExposureLimitModal, setShowExposureLimitModal] = useState(false)
  const [showCreditModal, setShowCreditModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState(null)

  // Data state
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  /* eslint-disable react-hooks/exhaustive-deps */
  const fetchUsers = async () => {
    setLoading(true)
    try {
      const data = await userService.getAllDetails(currentPage, entriesPerPage, searchValue)
      if (data.success && data.data && data.data.wallets) {
        setAccounts(data.data.wallets)
        // Assuming backend might return pagination info in future, for now just set accounts
        // If backend doesn't return total pages, we might need to adjust logic
      } else {
        setAccounts([])
      }
    } catch (err) {
      console.error('Error fetching users:', err)
      setAccounts([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [currentPage, entriesPerPage])

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    console.log('Export to PDF')
  }

  const handleExportExcel = () => {
    // TODO: Implement Excel export
    console.log('Export to Excel')
  }

  const handleLoad = () => {
    setCurrentPage(1)
    fetchUsers()
  }

  const handleReset = () => {
    setSearchValue('')
    setCurrentPage(1)
    // fetchUsers will be triggered by useEffect when searchValue changes if we add it to dependency, 
    // but here we want explicit load. 
    // Let's just reset search and call fetch
    setTimeout(() => fetchUsers(), 0)
  }

  const handleDepositClick = (account) => {
    setSelectedAccount(account)
    setShowDepositModal(true)
  }

  const handleWithdrawClick = (account) => {
    setSelectedAccount(account)
    setShowWithdrawModal(true)
  }

  const handleExposureLimitClick = (account) => {
    setSelectedAccount(account)
    setShowExposureLimitModal(true)
  }

  const handleCreditClick = (account) => {
    setSelectedAccount(account)
    setShowCreditModal(true)
  }

  const handlePasswordClick = (account) => {
    setSelectedAccount(account)
    setShowPasswordModal(true)
  }

  const handleChangeStatusClick = (account) => {
    setSelectedAccount(account)
    setShowChangeStatusModal(true)
  }



  const handleDepositSubmit = async (formData) => {
    console.log('Deposit submitted:', formData, 'for account:', selectedAccount)
    try {
      const payload = {
        userId: selectedAccount.user_id || selectedAccount.staff_id,
        amount: formData.amount,
        userType: selectedAccount.user_type,
        transactionPassword: formData.transactionPassword
      }

      const response = await walletService.addCash(payload)
      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: 'Deposit successful!'
        });
        setShowDepositModal(false)
        fetchUsers() // Refresh list
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Deposit failed: ' + (response.message || 'Unknown error')
        });
      }
    } catch (error) {
      console.error('Deposit error:', error)
      Toast.fire({
        icon: 'error',
        title: 'Deposit failed: ' + (error.response?.data?.message || error.message)
      });
    }
  }

  const handleWithdrawSubmit = async (formData) => {
    console.log('Withdraw submitted:', formData, 'for account:', selectedAccount)
    try {
      const payload = {
        userId: selectedAccount.user_id || selectedAccount.staff_id,
        amount: formData.amount,
        userType: selectedAccount.user_type,
        transactionPassword: formData.transactionPassword
      }

      const response = await walletService.subtractCash(payload)
      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: 'Withdraw successful!'
        });
        setShowWithdrawModal(false)
        fetchUsers() // Refresh list
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Withdraw failed: ' + (response.message || 'Unknown error')
        });
      }
    } catch (error) {
      console.error('Withdraw error:', error)
      Toast.fire({
        icon: 'error',
        title: 'Withdraw failed: ' + (error.response?.data?.message || error.message)
      });
    }
  }

  const handleExposureLimitSubmit = (formData) => {
    console.log('Exposure Limit submitted:', formData, 'for account:', selectedAccount)
    // TODO: Implement exposure limit API call
  }

  const handleCreditSubmit = async (formData) => {
    console.log('Credit submitted:', formData, 'for account:', selectedAccount)
    try {
      const payload = {
        userId: selectedAccount.user_id || selectedAccount.staff_id,
        amount: formData.amount, // Changed from newCredit to amount to match API
        userType: selectedAccount.user_type,
        transactionPassword: formData.transactionPassword
      }

      const response = await walletService.addCredit(payload)
      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: 'Credit added successful!'
        });
        setShowCreditModal(false)
        fetchUsers() // Refresh list
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Credit add failed: ' + (response.message || 'Unknown error')
        });
      }
    } catch (error) {
      console.error('Credit add error:', error)
      Toast.fire({
        icon: 'error',
        title: 'Credit add failed: ' + (error.response?.data?.message || error.message)
      });
    }
  }

  const handlePasswordSubmit = (formData) => {
    console.log('Password submitted:', formData, 'for account:', selectedAccount)
    // TODO: Implement password API call
  }

  const handleChangeStatusSubmit = async (formData) => {
    console.log('Change Status submitted:', formData, 'for account:', selectedAccount)
    try {
      const isUser = isUserRole(selectedAccount.user_type)
      const payload = {
        user_type: isUser ? 'USER' : 'STAFF',
        [isUser ? 'user_id' : 'staff_id']: selectedAccount.user_id || selectedAccount.staff_id,
        // Map userActive to status/active
        ...(isUser
          ? { status: formData.userActive ? 'Active' : 'InActive' }
          : { active: formData.userActive }
        ),
        transactionPassword: formData.transactionPassword
      }

      const response = await userService.updateStatusAndPassword(payload)

      if (response.success) {
        Toast.fire({
          icon: 'success',
          title: 'Status updated successfully'
        });
        setShowChangeStatusModal(false)
        fetchUsers() // Refresh list
      } else {
        Toast.fire({
          icon: 'error',
          title: 'Failed to update status: ' + (response.message || 'Unknown error')
        });
      }
    } catch (error) {
      console.error('Error updating status:', error)
      Toast.fire({
        icon: 'error',
        title: 'Failed to update status: ' + (error.response?.data?.message || error.message)
      });
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <Layout title="List of Clients - Diamond Admin">
      <div className="listing-grid">
        <div className="account-list">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">
                  Account List
                </h4>
                <div className="page-title-right">
                  <div className="d-inline-block">
                    <Link to="/admin/users/insertuser" className="btn btn-primary">
                      Add Account
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="account-list">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {/* PDF/Excel Export Buttons */}
                  <div className="row row5">
                    <div className="col-md-6 mb-2 search-form">
                      <div className="d-inline-block mr-2">
                        <button
                          type="button"
                          className="btn buttons-pdf btn-danger"
                          onClick={handleExportPDF}
                        >
                          <i className="far fa-file-pdf mr-1"></i>
                          PDF
                        </button>
                      </div>
                      <div className="d-inline-block">
                        <button
                          type="button"
                          className="btn buttons-excel btn-success"
                          onClick={handleExportExcel}
                        >
                          <i className="far fa-file-excel mr-1"></i>
                          Excel
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6 text-right mb-2"></div>
                  </div>

                  {/* Show Entries and Search */}
                  <div className="row">
                    <div className="col-sm-12 col-md-6">
                      <div id="tickets-table_length" className="dataTables_length">
                        <label className="d-inline-flex align-items-center">
                          Show&nbsp;
                          <select
                            className="custom-select custom-select-sm"
                            value={entriesPerPage}
                            onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="250">250</option>
                            <option value="500">500</option>
                            <option value="750">750</option>
                            <option value="1000">1000</option>
                          </select>
                          &nbsp;entries
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                        <label className="d-inline-flex align-items-center">
                          Search:
                          <input
                            name="searchuser"
                            type="search"
                            placeholder="Search..."
                            className="form-control form-control-sm ml-2 form-control py-1"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                          />
                          <button
                            type="button"
                            id="loaddata"
                            className="btn btn-primary ml-2"
                            onClick={handleLoad}
                          >
                            Load
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary ml-2"
                            onClick={handleReset}
                          >
                            Reset
                          </button>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive mb-0">
                    <div className="table no-footer list-clients table-responsive-sm">
                      <table
                        id="eventsListTbl"
                        role="table"
                        className="table b-table table-striped table-bordered"
                      >
                        <thead role="rowgroup" className="">
                          <tr role="row" className="">
                            <th role="columnheader" scope="col" className="position-relative">
                              <div>User Name</div>
                            </th>
                            <th role="columnheader" scope="col" className="position-relative text-right">
                              <div>Credit Referance</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>U st</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>B st</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>Balance</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>Exposure</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>Exposure Limit</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>Deafult (%)</div>
                            </th>
                            <th role="columnheader" scope="col" className="">
                              <div>Account Type</div>
                            </th>

                            <th role="columnheader" scope="col" className="">
                              <div>Action</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody role="rowgroup">
                          {loading ? (
                            <tr role="row" className="">
                              <td colSpan="8" className="text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : accounts.length === 0 ? (
                            <tr role="row" className="">
                              <td colSpan="8" className="text-center">
                                No accounts found
                              </td>
                            </tr>
                          ) : (
                            accounts.map((account, index) => (
                              <tr key={account.wallet_id} role="row" className="">
                                {index === 0 ? (
                                  <>
                                    <td aria-colindex="1" role="cell" className=""></td>
                                    <td aria-colindex="2" role="cell" className="">
                                      <p className="text-right mb-0 cp">{account.credit}</p>
                                    </td>
                                    <td aria-colindex="3" role="cell" className=""></td>
                                    <td aria-colindex="4" role="cell" className=""></td>
                                    <td aria-colindex="5" role="cell" className=""></td>
                                    <td aria-colindex="4" role="cell" className=""></td>
                                    <td aria-colindex="5" role="cell" className=""></td>
                                    <td aria-colindex="6" role="cell" className=""></td>
                                    <td aria-colindex="7" role="cell" className=""></td>
                                    <td aria-colindex="8" role="cell" className=""></td>

                                  </>
                                ) : (
                                  <>
                                    <td aria-colindex="1" role="cell" className="">
                                      <Link
                                        to={`/admin/child/${account.user_id || account.staff_id}`}
                                        className="wrape-text"
                                        title={account.username}
                                        target="_blank"
                                      >
                                        <span>{account.username}</span>
                                      </Link>
                                    </td>
                                    <td aria-colindex="2" role="cell" className="">
                                      <p className="text-right mb-0 cp">{account.credit}</p>
                                    </td>
                                    <td aria-colindex="3" role="cell" className="">
                                      <div className="custom-control custom-checkbox">
                                        <input
                                          type="checkbox"
                                          className="custom-control-input"
                                          id={`ust-${account.wallet_id}`}
                                          checked={account.status === 'Active'}
                                          disabled
                                        />
                                        <label className="custom-control-label" htmlFor={`ust-${account.wallet_id}`}></label>
                                      </div>
                                    </td>
                                    <td aria-colindex="4" role="cell" className="">
                                      <div className="custom-control custom-checkbox">
                                        <input
                                          type="checkbox"
                                          className="custom-control-input"
                                          id={`bst-${account.wallet_id}`}
                                          defaultChecked={true}
                                          disabled
                                        />
                                        <label className="custom-control-label" htmlFor={`bst-${account.wallet_id}`}></label>
                                      </div>
                                    </td>
                                    <td aria-colindex="5" role="cell" className="">
                                      <p className="text-right mb-0 cp">{account.inr_balance}</p>
                                    </td>
                                    <td aria-colindex="6" role="cell" className="">
                                      <p className="text-right mb-0 cp">{account.exposure}</p>
                                    </td>
                                    <td aria-colindex="7" role="cell" className="">{account.exposureLimit || 0}</td>
                                    <td aria-colindex="8" role="cell" className="">
                                      <p className="text-left mb-0">{account.percentage || 0}</p>
                                    </td>
                                    <td aria-colindex="9" role="cell" className="">{account.user_type}</td>
                                    <td aria-colindex="10" role="cell" className="">
                                      <div role="group" className="btn-group">
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handleDepositClick(account)}
                                          title="Deposit"
                                        >
                                          D
                                        </button>
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handleWithdrawClick(account)}
                                          title="Withdraw"
                                        >
                                          W
                                        </button>
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handleExposureLimitClick(account)}
                                          title="Exposure Limit"
                                        >
                                          L
                                        </button>
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handleCreditClick(account)}
                                          title="Credit"
                                        >
                                          C
                                        </button>
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handlePasswordClick(account)}
                                          title="Password"
                                        >
                                          P
                                        </button>
                                        <button
                                          type="button"
                                          className="btn action-button btn-secondary-action"
                                          onClick={() => handleChangeStatusClick(account)}
                                          title="Status"
                                        >
                                          S
                                        </button>
                                        <button type="button" className="btn action-button btn-secondary-action">More</button>
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="row pt-3">
                    <div className="col">
                      <div className="dataTables_paginate paging_simple_numbers float-right">
                        <ul className="pagination pagination-rounded mb-0">
                          <ul
                            role="menubar"
                            aria-label="Pagination"
                            className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
                          >
                            <li role="presentation" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                              >
                                «
                              </button>
                            </li>
                            <li role="presentation" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                ‹
                              </button>
                            </li>
                            <li role="presentation" className="page-item active">
                              <button
                                role="menuitemradio"
                                type="button"
                                aria-label={`Go to page ${currentPage}`}
                                className="page-link"
                              >
                                {currentPage}
                              </button>
                            </li>
                            <li role="presentation" className="page-item">
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(currentPage + 1)}
                              >
                                ›
                              </button>
                            </li>
                          </ul>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deposit Modal */}
      <TransactionModal
        show={showDepositModal}
        onHide={() => setShowDepositModal(false)}
        userData={selectedAccount}
        currentUser={user}
        mode="deposit"
        onSubmit={handleDepositSubmit}
        title="Deposit"
      />

      {/* Withdraw Modal */}
      <TransactionModal
        show={showWithdrawModal}
        onHide={() => setShowWithdrawModal(false)}
        userData={selectedAccount}
        currentUser={user}
        mode="withdraw"
        onSubmit={handleWithdrawSubmit}
        title="Withdraw"
      />

      {/* Exposure Limit Modal */}
      <ExposureLimitModal
        show={showExposureLimitModal}
        onHide={() => setShowExposureLimitModal(false)}
        userData={selectedAccount}
        onSubmit={handleExposureLimitSubmit}
      />

      {/* Credit Modal */}
      <CreditModal
        show={showCreditModal}
        onHide={() => setShowCreditModal(false)}
        userData={selectedAccount}
        onSubmit={handleCreditSubmit}
      />

      {/* Password Modal */}
      <PasswordModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        userData={selectedAccount}
        onSubmit={handlePasswordSubmit}
      />

      {/* Change Status Modal */}
      <ChangeStatusModal
        show={showChangeStatusModal}
        onHide={() => setShowChangeStatusModal(false)}
        userData={selectedAccount}
        onSubmit={handleChangeStatusSubmit}
      />
    </Layout>
  )
}

export default Users

